import { css } from "styled-components"
import { mdColors } from "./colors"

const corner = {
  default: 5,
  small: 3,
}

const clip = {
  default: css`
    clip-path: polygon(
      ${corner.default}px 0%,
      calc(100% - ${corner.default}px) 0%,
      100% ${corner.default}px,
      100% calc(100% - ${corner.default}px),
      calc(100% - ${corner.default}px) 100%,
      ${corner.default}px 100%,
      0% calc(100% - ${corner.default}px),
      0% ${corner.default}px
    );
  `,
  small: css`
    clip-path: polygon(
      ${corner.small}px 0%,
      calc(100% - ${corner.small}px) 0%,
      100% ${corner.small}px,
      100% calc(100% - ${corner.small}px),
      calc(100% - ${corner.small}px) 100%,
      ${corner.small}px 100%,
      0% calc(100% - ${corner.small}px),
      0% ${corner.small}px
    );
  `,
}

export const defaultTheme = {
  colors: {
    primary: "rgb(212,152,8)",
    primaryHighlight: "rgb(238,176,50)",
    primaryDark: "rgba(182,134,35,0.81)",
    primaryFaded: "rgb(182,134,35)",
    secondary: "rgb(80,193,223)",
    secondaryHighlight: "rgb(119,227,255)",
    secondaryDark: "rgb(12,132,165)",
    delta: "#0844D4",
    green: "#08D498",
    greenDark: "#0A9E6E",
    purple: "#9808D4",
    background: "rgb(17,17,17)",
    backgroundHighlight: "rgb(51,51,51)",
    danger: mdColors.Red["500"],
    warning: mdColors.Orange["500"],
    white: "white",
  },
  width: {
    default: 960,
    narrow: 400,
  },
  clip,
  fontFamily: "'Electrolize', 'Exo 2', sans-serif",
  headingFontFamily: undefined as string | undefined,
  glowColor: undefined as string | undefined,
}

export type MapSpotTheme = typeof defaultTheme

export const scifiTheme: MapSpotTheme = {
  colors: {
    primary: "#00f0ff",
    primaryHighlight: "#4dffff",
    primaryDark: "#008b99",
    primaryFaded: "#007a8a",
    secondary: "#00ff88",
    secondaryHighlight: "#66ffbb",
    secondaryDark: "#00994d",
    delta: "#ff3366",
    green: "#00ff88",
    greenDark: "#00cc66",
    purple: "#bf00ff",
    background: "rgb(8,12,18)",
    backgroundHighlight: "rgb(20,30,45)",
    danger: "#ff3333",
    warning: "#ff9900",
    white: "#e0e8f0",
  },
  width: { default: 960, narrow: 400 },
  clip,
  fontFamily: "'Share Tech Mono', monospace",
  headingFontFamily: "'Orbitron', sans-serif",
  glowColor: "rgba(0, 240, 255, 0.4)",
}

export const fantasyTheme: MapSpotTheme = {
  colors: {
    primary: "#c9a84c",
    primaryHighlight: "#e0c76a",
    primaryDark: "#8b6f2a",
    primaryFaded: "#a68b3c",
    secondary: "#6db89e",
    secondaryHighlight: "#8fd4b8",
    secondaryDark: "#3d7a62",
    delta: "#8844cc",
    green: "#5dba6f",
    greenDark: "#3a8a4a",
    purple: "#9b59b6",
    background: "rgb(20,16,12)",
    backgroundHighlight: "rgb(40,34,26)",
    danger: "#cc4444",
    warning: "#d4952a",
    white: "#ede4d4",
  },
  width: { default: 960, narrow: 400 },
  clip,
  fontFamily: "'Cinzel', 'Electrolize', serif",
  headingFontFamily: "'Cinzel Decorative', 'Cinzel', serif",
  glowColor: "rgba(201, 168, 76, 0.3)",
}

export const horrorTheme: MapSpotTheme = {
  colors: {
    primary: "#cc2222",
    primaryHighlight: "#ee4444",
    primaryDark: "#881111",
    primaryFaded: "#993333",
    secondary: "#8866aa",
    secondaryHighlight: "#aa88cc",
    secondaryDark: "#553377",
    delta: "#ff6633",
    green: "#44aa55",
    greenDark: "#338844",
    purple: "#9933cc",
    background: "rgb(12,8,8)",
    backgroundHighlight: "rgb(30,18,18)",
    danger: "#ff2222",
    warning: "#cc6600",
    white: "#d8cccc",
  },
  width: { default: 960, narrow: 400 },
  clip,
  fontFamily: "'Creepster', 'Electrolize', cursive",
  headingFontFamily: "'Nosifer', 'Creepster', cursive",
  glowColor: "rgba(204, 34, 34, 0.35)",
}

export const openworldTheme: MapSpotTheme = {
  colors: {
    primary: "#4a9eff",
    primaryHighlight: "#70b4ff",
    primaryDark: "#2a6ecc",
    primaryFaded: "#3880dd",
    secondary: "#ff9944",
    secondaryHighlight: "#ffb070",
    secondaryDark: "#cc7722",
    delta: "#22cc88",
    green: "#44cc66",
    greenDark: "#2a9944",
    purple: "#aa44ff",
    background: "rgb(12,16,22)",
    backgroundHighlight: "rgb(24,32,44)",
    danger: "#ff4444",
    warning: "#ffaa22",
    white: "#e4e8f0",
  },
  width: { default: 960, narrow: 400 },
  clip,
  fontFamily: "'Rajdhani', 'Electrolize', sans-serif",
  headingFontFamily: "'Rajdhani', sans-serif",
  glowColor: "rgba(74, 158, 255, 0.3)",
}

export const darkfantasyTheme: MapSpotTheme = {
  ...defaultTheme,
  colors: {
    ...defaultTheme.colors,
    primary: "rgb(212,152,8)",
    background: "rgb(14,12,10)",
    backgroundHighlight: "rgb(32,28,22)",
  },
  glowColor: "rgba(212, 152, 8, 0.25)",
}

export const themes: Record<string, MapSpotTheme> = {
  default: defaultTheme,
  scifi: scifiTheme,
  fantasy: fantasyTheme,
  horror: horrorTheme,
  openworld: openworldTheme,
  darkfantasy: darkfantasyTheme,
}

export function getTheme(themeId?: string): MapSpotTheme {
  return themes[themeId || "default"] || defaultTheme
}
